import { FC, useState } from 'react';
import { Popover } from '@headlessui/react';
import moment from 'moment-timezone';

import { ReactComponent as MoreIcon } from 'assets/icons/more.svg';
import ConfirmationModal from 'components/ConfirmationModal/ConfirmationModal';
import { renderToast } from 'components/Toast/Toast';
import useMatchMutate from 'hooks/useMatchMutate';
import axiosInstance from 'apis/axiosInstance';
import { BAD_REQUEST } from 'utils/constants/statusCode';
import {
  DATE_TIME_FORMAT,
  datetimeFormat,
  parseDateStringWithoutAffectTz,
} from 'utils/datetime';
import {
  IPaymentAuthorization,
  IStripeLinkResponse,
  PaymentStatus,
} from 'interfaces/stripe';

// Revalidates both the "Appts With Holds" list and the stats tiles, whose SWR
// keys both start with "/payments".
const PAYMENTS_KEY_MATCHER = /\/payments/;

const getErrorMessage = (error: any) =>
  error?.response?.status === BAD_REQUEST
    ? error.response?.data?.message
    : 'An error has occurred. Please try again.';

const hasAppointmentStarted = ({
  appointmentDate,
  startTime,
  timezone,
}: IPaymentAuthorization) => {
  if (!appointmentDate || !startTime || !timezone) {
    return false;
  }

  const date = datetimeFormat({
    dateString: String(appointmentDate),
    format: DATE_TIME_FORMAT.APPOINTMENT_DATE,
    pattern: DATE_TIME_FORMAT.APPOINTMENT_DATE,
  });

  return parseDateStringWithoutAffectTz(
    `${date} ${startTime}`,
    `${DATE_TIME_FORMAT.APPOINTMENT_DATE} ${DATE_TIME_FORMAT.HH_MM_SS}`,
    timezone
  ).isSameOrBefore(moment());
};

interface ThreeDotsOptionProps {
  authorization: IPaymentAuthorization;
}

const OPTIONS = {
  RELEASE: { label: 'Release hold', value: 'release' },
  CHARGE: { label: 'Charge payment', value: 'charge' },
  REFUND: { label: 'Refund', value: 'refund' },
  CHARGE_OFF_SESSION: {
    label: 'Charge fine',
    value: 'charge-off-session',
  },
  VIEW_DISPUTE: {
    label: 'View dispute in Stripe ↗',
    value: 'view-dispute',
  },
};

const { RELEASE, CHARGE, REFUND, CHARGE_OFF_SESSION, VIEW_DISPUTE } = OPTIONS;

type PaymentOption = typeof RELEASE;

const ThreeDotsOption: FC<ThreeDotsOptionProps> = ({ authorization }) => {
  const [confirmModal, setConfirmModal] = useState<PaymentOption | null>(null);
  const matchMutate = useMatchMutate();

  const patientName = authorization.patient?.name ?? 'this patient';

  const getOptions = (): PaymentOption[] => {
    if (authorization.isDisputed) {
      return [VIEW_DISPUTE];
    }

    if (authorization.status === PaymentStatus.HELD) {
      return [RELEASE, CHARGE];
    }

    if (authorization.status === PaymentStatus.CAPTURED) {
      return [REFUND];
    }

    if (authorization.status === PaymentStatus.AUTH_EXPIRED) {
      if (authorization.noShowFeeChargedAt) {
        return [];
      }

      return hasAppointmentStarted(authorization) ? [CHARGE_OFF_SESSION] : [];
    }

    return [];
  };

  const options = getOptions();

  const onClose = () => setConfirmModal(null);

  const handleViewDispute = async () => {
    try {
      const { data } = await axiosInstance.get<IStripeLinkResponse>(
        '/clinics/stripe/login-link'
      );
      window.open(data.url, '_blank', 'noopener,noreferrer');
    } catch (error) {
      renderToast({
        message: 'Could not open the Stripe dashboard. Please try again.',
        type: 'error',
      });
    }
  };

  const handleClickOption = (option: PaymentOption) => {
    if (option === VIEW_DISPUTE) {
      return handleViewDispute();
    }

    setConfirmModal(option);
  };

  const handleRelease = async () => {
    try {
      await axiosInstance.post(
        `/payments/bookings/${authorization.bookingId}/release`
      );
      renderToast({ message: 'Hold released successfully', type: 'success' });
      await matchMutate(PAYMENTS_KEY_MATCHER);
      onClose();
    } catch (error) {
      renderToast({ message: getErrorMessage(error), type: 'error' });
    }
  };

  const handleCharge = async () => {
    try {
      await axiosInstance.post(
        `/payments/bookings/${authorization.bookingId}/capture`
      );
      renderToast({ message: 'Payment charged successfully', type: 'success' });
      await matchMutate(PAYMENTS_KEY_MATCHER);
      onClose();
    } catch (error) {
      renderToast({ message: getErrorMessage(error), type: 'error' });
    }
  };

  const handleRefund = async () => {
    try {
      await axiosInstance.post(
        `/payments/bookings/${authorization.bookingId}/refund`
      );
      renderToast({
        message: 'Payment refunded successfully',
        type: 'success',
      });
      await matchMutate(PAYMENTS_KEY_MATCHER);
      onClose();
    } catch (error) {
      renderToast({ message: getErrorMessage(error), type: 'error' });
    }
  };

  const handleChargeOffSession = async () => {
    try {
      await axiosInstance.post(
        `/payments/bookings/${authorization.bookingId}/charge-no-show`
      );
      renderToast({
        message: 'No-show fee charged successfully',
        type: 'success',
      });
      await matchMutate(PAYMENTS_KEY_MATCHER);
      onClose();
    } catch (error) {
      renderToast({ message: getErrorMessage(error), type: 'error' });
    }
  };

  if (options.length === 0) {
    return null;
  }

  return (
    <>
      {confirmModal === RELEASE && (
        <ConfirmationModal
          title="Release hold?"
          description={`Are you sure you want to release the hold for ${patientName}?`}
          onClose={onClose}
          onSubmit={handleRelease}
          submitBtnTitle="Yes, release"
        />
      )}
      {confirmModal === CHARGE && (
        <ConfirmationModal
          title="Charge payment?"
          description={`Are you sure you want to charge the held amount for ${patientName}?`}
          onClose={onClose}
          onSubmit={handleCharge}
          submitBtnTitle="Yes, charge"
        />
      )}
      {confirmModal === REFUND && (
        <ConfirmationModal
          title="Refund payment?"
          description={`Are you sure you want to refund the payment for ${patientName}?`}
          onClose={onClose}
          onSubmit={handleRefund}
          submitBtnTitle="Yes, refund"
        />
      )}
      {confirmModal === CHARGE_OFF_SESSION && (
        <ConfirmationModal
          title="Charge fine?"
          description={`The authorization hold for ${patientName} has expired, so the fee will be charged to their saved card. Are you sure you want to charge it?`}
          onClose={onClose}
          onSubmit={handleChargeOffSession}
          submitBtnTitle="Yes, charge"
        />
      )}
      <Popover className="relative hidden group-hover:block">
        <Popover.Button className="flex items-center flex-shrink-0 disabled:cursor-not-allowed disabled:opacity-30">
          <MoreIcon className="h-2.2 flex-shrink-0" />
        </Popover.Button>
        <Popover.Panel className="flex flex-col gap-y-1.6 absolute py-2 right-0 z-10 min-w-[16.5rem] whitespace-nowrap bg-white shadow-primary rounded-[0.8rem] mt-0.6">
          {options.map((option) => (
            <Popover.Button
              key={option.value}
              className="w-full px-2 text-left hover:bg-opacity-10 hover:text-magenta"
              onClick={() => handleClickOption(option)}
            >
              {option.label}
            </Popover.Button>
          ))}
        </Popover.Panel>
      </Popover>
    </>
  );
};

export default ThreeDotsOption;
